import { Box, Tab, styled } from "@mui/material";
import { Suspense, lazy, memo, useMemo } from "react";
import { Row, SmTextMd, Tabs } from "Components";
import { useIntl } from "react-intl";
import { DIRECT_PATHS, PATHS, SEARCH_PARAMS } from "constants/routes";
import { useLocation, useParams } from "react-router-dom";
import useNavigate from "hooks/useNavigate";
import ListingFilters from "Components/ListingFilters";
import { ParamsType } from "types/params";
import { COLORS } from "constants/layout";
import { ALink } from "Components/link";
import useBreakpoints from "hooks/useBreakpoint";
import { getSearchParam } from "utils/searchParamsUtils";
import { objectToQueryString } from "helpers/search";
import Breadcrumbs from "Components/breadcrumbs";
import { useCommonData } from "providers/common-data";

const SearchProducts = lazy(() => import("./SearchProducts"));
const SearchSuppliers = lazy(() => import("./SearchSuppliers"));

type Props = {};

const SearchResult = (props: Props) => {
  const t = useIntl();
  const { isMobile } = useBreakpoints();
  const { history } = useNavigate();
  const { pathname } = useLocation();
  const { categoryId = "", countryId } = useParams<ParamsType>();
  const { breadscumbs = [] } = useCommonData();
  const categoryClass = pathname.split("/")[1];
  const searchParam = getSearchParam(SEARCH_PARAMS.keyword);

  const contentTabOptions = useMemo(() => {
    const searchRoutes = [
      PATHS.search.replace("/", ""),
      PATHS.searchSuppliers.replace("/", ""),
    ];
    const categoryRoutes = [
      PATHS.category.replace("/", ""),
      PATHS.categorySupplier.replace("/", ""),
    ];
    const dynamicType = {
      category:
        searchRoutes.indexOf(categoryClass) > -1
          ? searchRoutes[0]
          : categoryRoutes[0],
      categorySupplier:
        searchRoutes.indexOf(categoryClass) > -1
          ? searchRoutes[1]
          : categoryRoutes[1],
    };
    return [
      {
        label: "suppliers",
        href:
          searchRoutes.indexOf(categoryClass) > -1
            ? searchRoutes[1]
            : DIRECT_PATHS.categorySupplier(categoryId, countryId),
        type: dynamicType.categorySupplier,
      },
      {
        label: "products",
        href:
          searchRoutes.indexOf(categoryClass) > -1
            ? searchRoutes[0]
            : DIRECT_PATHS.category(categoryId, countryId),
        type: dynamicType.category,
      },
    ];
  }, [categoryClass, categoryId]);

  const buyLeadsHref = `${
    categoryId ? DIRECT_PATHS.categoryRfq(categoryId) : PATHS.buyleads
  }${
    searchParam
      ? `?${objectToQueryString({ [SEARCH_PARAMS.keyword]: searchParam })}`
      : ""
  }`;

  const Content = useMemo(() => {
    if (categoryClass === contentTabOptions?.[0].type) {
      return SearchSuppliers;
    }
    return SearchProducts;
  }, [categoryClass]);

  const onClickTab = (href: string) => {
    history.push({
      pathname: href,
      search: searchParam
      ? `?${objectToQueryString({ [SEARCH_PARAMS.keyword]: searchParam })}`
      : "",
    });
  };

  return (
    <StyledGrid>
      <Breadcrumbs data={breadscumbs} />

      <Row className="content-tabs" justifyContent="space-between" my={1}>
        <Row>
          <Tabs value={categoryClass}>
            {contentTabOptions.map(({ type, label, href }) => (
              <Tab
                onClick={() => onClickTab(href)}
                key={type}
                label={t.formatMessage({ id: label })}
                value={type}
              />
            ))}
          </Tabs>
          {!isMobile && (
            <ALink href={buyLeadsHref} hideUnderline openNewTab>
              <SmTextMd color={COLORS.green[600]}>
                {t.formatMessage({ id: "buying_leads" })}
              </SmTextMd>
            </ALink>
          )}
        </Row>
        {isMobile && <ListingFilters drawerMode />}
      </Row>

      <Suspense fallback={null}>
        <Content />
      </Suspense>
    </StyledGrid>
  );
};

const StyledGrid = styled(Box)(({ theme }) => ({
  ".content-tabs": {
    backgroundColor: COLORS.app.bg,
    zIndex: 5,
  },
  [theme.breakpoints.down("md")]: {
    ".content-tabs": {},
  },
}));

export default memo(SearchResult);
